/**
 * SecurityTreePanel — Full panel for managing user plant access.
 *
 * Composes:
 * - SecurityTreeFilterBar (geographic filter + search)
 * - SecurityTree (virtualized tree with 5-state icons)
 * - Save/Cancel action bar with dirty state detection
 *
 * This is the top-level component that a page would render.
 * It consumes API data and exposes a save callback.
 */

import { useState } from 'react';
import Button from '@mui/material/Button';
import type { PlantAccessEntry, SecurityTreeNodeDto } from './types';
import { useSecurityTree } from './useSecurityTree';
import { SecurityTree } from './SecurityTree';
import { SecurityTreeFilterBar } from './SecurityTreeFilterBar';

interface SecurityTreePanelProps {
  /** Flat tree data from the API (GetSecurityTree query). */
  data: SecurityTreeNodeDto[] | undefined;
  /** Called when user clicks Save. Receives only changed plant access entries. */
  onSave: (entries: PlantAccessEntry[]) => void;
  /** Whether parent-managed settings outside the tree have unsaved changes. */
  externalDirty?: boolean;
  /** Called when the panel Cancel button should also reset parent-managed state. */
  onExternalCancel?: () => void;
  /** Whether a save operation is in progress. */
  saving?: boolean;
  /** Height of the tree area in pixels. */
  treeHeight?: number;
}

export function SecurityTreePanel({
  data,
  onSave,
  externalDirty = false,
  onExternalCancel,
  saving = false,
  treeHeight = 500,
}: SecurityTreePanelProps) {
  const treeState = useSecurityTree(data);
  const [searchTerm, setSearchTerm] = useState('');
  const isDirty = treeState.isDirty || externalDirty;

  const handleSave = () => {
    const payload = treeState.getSavePayload();
    onSave(payload);
  };

  const handleCancel = () => {
    treeState.reset();
    setSearchTerm('');
    onExternalCancel?.();
  };

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-foreground-faded text-sm">
        No access data available.
      </div>
    );
  }

  return (
    <div className="flex flex-col border border-border rounded-lg shadow-sm">
      {/* Filter bar */}
      <SecurityTreeFilterBar
        tree={treeState.tree}
        filter={treeState.filter}
        onFilterChange={treeState.setFilter}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      {/* Tree */}
      <div className="flex-1 overflow-hidden">
        <SecurityTree
          treeState={treeState}
          height={treeHeight}
          searchTerm={searchTerm || undefined}
        />
      </div>

      {/* Action bar */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-container-bg rounded-b-lg">
        <div className="text-sm text-foreground-faded">
          {isDirty && (
            <span className="text-warning font-medium">Unsaved changes</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outlined"
            size="small"
            onClick={handleCancel}
            disabled={!isDirty || saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={!isDirty || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
